import { useRef, useEffect, useCallback } from 'react';

interface UseAutoScrollOptions {
  edgeSize?: number;
  maxSpeed?: number;
  direction?: 'vertical' | 'horizontal' | 'both';
  disabled?: boolean;
}

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

export function useAutoScroll<T extends HTMLElement = HTMLElement>(
  scrollRef: React.RefObject<T>,
  isDragging: boolean,
  { edgeSize = 120, maxSpeed = 18, direction = 'vertical', disabled = false }: UseAutoScrollOptions = {},
) {
  const rafRef = useRef<number | null>(null);
  const pointerRef = useRef<{ x: number; y: number } | null>(null);

  const tick = useCallback(() => {
    const el = scrollRef.current;
    const pointer = pointerRef.current;

    if (!el) {
      rafRef.current = null;
      return;
    }

    if (!pointer) {
      rafRef.current = requestAnimationFrame(tick);
      return;
    }

    const rect = el.getBoundingClientRect();
    let scrollByY = 0;
    let scrollByX = 0;

    if (direction !== 'horizontal') {
      const distTop = pointer.y - rect.top;
      const distBottom = rect.bottom - pointer.y;

      if (distTop < edgeSize) {
        const intensity = clamp(1 - distTop / edgeSize, 0, 1);
        scrollByY = -Math.ceil(maxSpeed * intensity);
      } else if (distBottom < edgeSize) {
        const intensity = clamp(1 - distBottom / edgeSize, 0, 1);
        scrollByY = Math.ceil(maxSpeed * intensity);
      }
    }

    if (direction !== 'vertical') {
      const distLeft = pointer.x - rect.left;
      const distRight = rect.right - pointer.x;

      if (distLeft < edgeSize) {
        const intensity = clamp(1 - distLeft / edgeSize, 0, 1);
        scrollByX = -Math.ceil(maxSpeed * intensity);
      } else if (distRight < edgeSize) {
        const intensity = clamp(1 - distRight / edgeSize, 0, 1);
        scrollByX = Math.ceil(maxSpeed * intensity);
      }
    }

    if (scrollByX !== 0 || scrollByY !== 0) {
      el.scrollBy({ top: scrollByY, left: scrollByX });
    }

    rafRef.current = requestAnimationFrame(tick);
  }, [scrollRef, edgeSize, maxSpeed, direction]);

  const handlePointerMove = useCallback((e: PointerEvent) => {
    pointerRef.current = {
      x: clamp(e.clientX, 0, window.innerWidth),
      y: clamp(e.clientY, 0, window.innerHeight),
    };
  }, []);

  useEffect(() => {
    if (disabled || !isDragging) {
      return undefined;
    }

    window.addEventListener('pointermove', handlePointerMove, { capture: true });
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove, { capture: true });
      pointerRef.current = null;
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [isDragging, disabled, tick, handlePointerMove]);
}
